import React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import CloseIcon from "@mui/icons-material/Close";
import ReadOnlyField from "../create_training_content/FormFieldReadOnly";
import {
  visibleFields,
  buildFieldsById,
  resolvePiping,
} from "../create_training_content/formLogic";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 640,
  maxHeight: "90vh",
  bgcolor: "background.paper",
  borderRadius: "16px",
  boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
  overflowY: "auto",
  border: "none",
};

function formatDateReadable(dateString) {
  if (!dateString) return "";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// Renders a single learner's form submission in the form's own layout, with
// their answers filled in read-only — the form-as-taken (Records review).
function FormSubmissionReviewModal({ open, onClose, record, submission }) {
  const scoring = !!record?.scoring_enabled;
  // Prefer the field snapshot stored with this submission so it renders exactly
  // as it was taken, even if the form was edited later.
  const fields =
    submission?.fields && submission.fields.length
      ? submission.fields
      : record?.fields || [];
  const responses = submission?.responses || {};
  // Show the form as the respondent saw it: fields hidden by a display
  // condition are omitted, and piped labels resolve to their answers.
  const shownFields = visibleFields(fields, responses);
  const fieldsById = buildFieldsById(fields);
  const withPiping = (f) => ({
    ...f,
    label: resolvePiping(f.label, responses, fieldsById, "[answer]"),
    help: resolvePiping(f.help, responses, fieldsById, "[answer]"),
  });

  return (
    <Modal open={open && !!submission} onClose={onClose} disableEnforceFocus>
      <Box sx={style}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 z-10 rounded-t-2xl">
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <h3 className="text-xl font-bold text-slate-900 truncate">
                {submission?.employee_name || "Submission"}
              </h3>
              <p className="text-sm text-slate-600 truncate">
                {record?.name || "Form"}
              </p>
            </div>
            <button
              className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-500 shrink-0"
              onClick={onClose}
            >
              <CloseIcon />
            </button>
          </div>
        </div>

        {submission && (
          <div className="px-6 py-5">
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-2 mb-4 text-xs">
              {scoring && submission.score !== null && submission.score !== undefined && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-700">
                  Score {submission.score}
                </span>
              )}
              {scoring && submission.is_passed === true && (
                <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-medium text-emerald-700">
                  Passed
                </span>
              )}
              {scoring && submission.is_passed === false && (
                <span className="rounded-full bg-red-100 px-2 py-0.5 font-medium text-red-700">
                  Not passed
                </span>
              )}
              {submission.completed_by && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
                  Completed by {submission.completed_by}
                </span>
              )}
              {submission.date_completed && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
                  {formatDateReadable(submission.date_completed)}
                </span>
              )}
            </div>

            {fields.length === 0 ? (
              <p className="text-sm text-slate-400">No fields on this form.</p>
            ) : (
              <div className="space-y-4">
                {shownFields.map((f) => (
                  <ReadOnlyField
                    key={f.id}
                    field={withPiping(f)}
                    value={responses[f.id]}
                    answered
                    showScoring
                  />
                ))}
              </div>
            )}
          </div>
        )}
      </Box>
    </Modal>
  );
}

export default FormSubmissionReviewModal;
